import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { styled as MUIStyled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeepResearchProgress from '../chat/DeepResearchProgress';
import PanoramaProgress from '../chat/PanoramaProgress';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
// docx generation now handled by backend
import { saveAs } from 'file-saver';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import ShareModal from './ShareModal';
import GeneratedFilePreview from '../chat/GeneratedFilePreview';
import ImageGeneratingSkeleton from '../chat/ImageGeneratingSkeleton';
import MessageComposerPreview from '../chat/MessageComposerPreview';
import PanoramaConfirmation from '../chat/PanoramaConfirmation';
import DeepResearchConfirmation from '../chat/DeepResearchConfirmation';
import PanoramaDebug from '../chat/PanoramaDebug';
import ContentItemErrorBoundary from './ContentItemErrorBoundary';
import CodeExecutionBlock from '../chat/CodeExecutionBlock';
import ToolActionBlock from '../chat/ToolActionBlock';
import SearchResultsBlock from '../chat/SearchResultsBlock';
import MapBlock from '../chat/MapBlock';
import ArtifactCard from '../artifacts/ArtifactCard';
import TodoListBlock from '../chat/TodoListBlock';
import { hasVisibleTodoCard, lastTodoMessageIndex, shouldShowTodoCard } from '../../utils/todoCards';
import ExtractingFilesBlock from '../chat/ExtractingFilesBlock';
import ThinkingGroup from '../chat/ThinkingGroup';
import ActivityStatus from '../chat/ActivityStatus';
import { useStableDisclosure } from '../../utils/stableDisclosure';
import { computeThinkingRuns, groupedIndexSet, thinkingRunPending, hasVisibleItemsAfter, trailingThinkingRunActive, setThinkingScope, getThinkingScope, assistantRowRendersContent, setCompactActivity, isCompactActivity, compactAgentContent } from '../../utils/thinkingGroup';
import PacedText from '../chat/PacedText';
import CollapsibleUserMessage from '../chat/CollapsibleUserMessage';
import { Reference, GeneratedFile, FileRef, FileRefContentItem, ContentItem, TextContentItem, FileContentItem, CodeExecutionContentItem, ToolActionContentItem, SearchResultsContentItem, MapContentItem, ArtifactContentItem, TodoListContentItem, ActiveComparison, ComparisonSlot, ToolCallEntry } from '../../shared/interfaces';
import LoadingMessageExtracted from './LoadingMessage';
import ErrorMessageExtracted from './ErrorMessage';
import ToolCallsDebugPanel from '../chat/ToolCallsDebugPanel';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { preprocessingLatex, getMessageTextContent } from './utils';
import { useRenderTracker } from '../../utils/perfProbe';
import QueuedMessageRow from '../chat/QueuedMessageRow';
import { queuedForChat } from '../../utils/messageQueue';

// Types
type PlaceholderVariant = 'pdf' | 'image' | 'file' | 'textCsv' | 'textTxt' | 'textMd' | 'docx' | 'xlsx';

// Utility functions
const getPlaceholderVariant = (filename: string): PlaceholderVariant => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (ext === 'csv') return 'textCsv';
  if (ext === 'txt') return 'textTxt';
  if (ext === 'md') return 'textMd';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx') return 'xlsx';
  return 'file';
};

const isPdfFile = (filename: string) => filename.toLowerCase().endsWith('.pdf');
const isImageFile = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
};

const shouldShowPageCount = (filename: string, pageCount?: number) =>
  isPdfFile(filename) && typeof pageCount === 'number' && pageCount > 0;

const formatPageCountLabel = (count: number) =>
  count === 1 ? '1 página' : `${count} páginas`;

const placeholderTypeLabels: Record<PlaceholderVariant, string> = {
  pdf: 'PDF',
  image: 'Imagem',
  file: 'Arquivo',
  textCsv: 'CSV',
  textTxt: 'Texto',
  textMd: 'Markdown',
  docx: 'Word',
  xlsx: 'Excel',
};

const getPlaceholderColors = (theme: any, variant: PlaceholderVariant) => {
  const colorMap: Record<PlaceholderVariant, { cardBg: string; border: string; iconBg: string; iconColor: string; nameColor: string; typeColor: string }> = {
    pdf: {
      cardBg: theme.file_pdf_card_bg,
      border: theme.file_pdf_border,
      iconBg: theme.file_pdf_icon_bg,
      iconColor: theme.file_pdf_icon_color,
      nameColor: theme.file_pdf_name_color,
      typeColor: theme.file_pdf_type_color,
    },
    image: {
      cardBg: theme.file_image_card_bg,
      border: theme.file_image_border,
      iconBg: theme.file_image_icon_bg,
      iconColor: theme.file_image_icon_color,
      nameColor: theme.file_image_name_color,
      typeColor: theme.file_image_type_color,
    },
    textCsv: {
      cardBg: theme.file_csv_card_bg,
      border: theme.file_csv_border,
      iconBg: theme.file_csv_icon_bg,
      iconColor: theme.file_csv_icon_color,
      nameColor: theme.file_csv_name_color,
      typeColor: theme.file_csv_type_color,
    },
    textTxt: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    textMd: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    docx: {
      cardBg: theme.file_docx_card_bg,
      border: theme.file_docx_border,
      iconBg: theme.file_docx_icon_bg,
      iconColor: theme.file_docx_icon_color,
      nameColor: theme.file_docx_name_color,
      typeColor: theme.file_docx_type_color,
    },
    xlsx: {
      cardBg: theme.file_xlsx_card_bg,
      border: theme.file_xlsx_border,
      iconBg: theme.file_xlsx_icon_bg,
      iconColor: theme.file_xlsx_icon_color,
      nameColor: theme.file_xlsx_name_color,
      typeColor: theme.file_xlsx_type_color,
    },
    file: {
      cardBg: theme.file_generic_card_bg,
      border: theme.file_generic_border,
      iconBg: theme.file_generic_icon_bg,
      iconColor: theme.file_generic_icon_color,
      nameColor: theme.file_generic_name_color,
      typeColor: theme.file_generic_type_color,
    },
  };
  return colorMap[variant];
};

const getVariantIcon = (variant: PlaceholderVariant) => {
  switch (variant) {
    case 'pdf': return <PdfPlaceholderIcon />;
    case 'image': return <ImagePlaceholderIcon />;
    case 'textCsv':
    case 'textTxt':
    case 'textMd': return <TextPlaceholderIcon />;
    default: return <GenericPlaceholderIcon />;
  }
};

// Custom Tooltip
const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
});

// Styled Components
const UserMessageStrip = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  background-color: transparent;
  padding: 0px;
  /* Keep the previous assistant turn fully above the viewport when this row is
     aligned below the fixed share control. The gap is real layout space, so a
     transparent header never has to mask content behind it. */
  margin: 22px 0 0;
  position: relative;
  content-visibility: auto;
  contain-intrinsic-size: auto 72px;
`;

const UserMessageStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
  max-width: 100%;

  /* Accessible default for touch and other inputs without reliable hover. */
  .user-message-actions {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 120ms ease;
  }

  @media (hover: hover) and (pointer: fine) {
    .user-message-actions {
      opacity: 0;
      pointer-events: none;
    }

    &:hover .user-message-actions,
    &:focus-within .user-message-actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const UserMessageStripContentArea = styled.div`
  color: ${({theme})=> theme.font_color};
  width: auto;
  max-width: 100%;
  margin: 0px;
  justify-content: flex-end;
  background-color: ${({theme})=> theme.user_message_background};
  border-radius: 20px;
  white-space: normal;
  overflow-wrap: break-word;
  @media (max-width: 900px) {
    margin-right: 10px;
    max-width: 100%;
    white-space: normal;
  }
`;

const MessageStripTextArea = styled.div`
  display: flex;
  flex-direction: column;
  font-align: left;
  font-size: 1rem;
  line-height: 1.8rem;
  padding: 10px;
  max-width: 100%;
  overflow-wrap: break-word;
  font-family: 'Sora', 'Lexend', sans-serif;
  @media (max-width: 600px) {
    max-width: 100%;
  }
  & p { white-space: pre-line; }
`;

const UserMessageStripTextArea = styled(MessageStripTextArea)`
  max-width: 100%;
  & p {
    white-space: pre-wrap;
  }
`;

const UserMessageMarkdown = styled(ReactMarkdown)`
  white-space: pre-wrap;
  & p { white-space: pre-wrap; margin: 0; }
  & .katex { font-size: 1.05em; }
  & .katex-display { text-align: center; margin: 8px 0; }
  & code { background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
`;

const EditTextArea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background-color: ${({theme})=> theme.user_message_background};
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
  margin: 0;
  overflow: hidden;
  white-space: pre-wrap;
`;

const EditButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const EditButton = styled.button`
  padding: 0.5rem 1.3rem;
  border-radius: 20px;
  border: 1px solid ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8'};
  background-color: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({theme}) => theme.font_color};
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background-color: ${({theme}) => theme.hover_overlay_light};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Primario preenchido no acento verde da familia dos cards de agente —
     o outline lilas anterior tinha contraste baixo e lia como desabilitado. */
  &[type="submit"] {
    border-color: transparent;
    background-color: #047857;
    color: #fff;
  }

  &[type="submit"]:hover:not(:disabled) {
    background-color: #065f46;
    border-color: transparent;
    color: #fff;
  }

  &[type="button"] {
    color: ${({theme}) => theme.font_color};
    border-color: ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : '#94a3b8'};

    &:hover:not(:disabled) {
      border-color: ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'};
    }
  }
`;

const UserMessageActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  align-self: flex-end;
  margin-top: 10px;
  margin-right: 4px;
`;

const EditButtonIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  box-sizing: border-box;
  background-color: transparent;
  border: none;
  padding: 5px;
  margin-top: 0;
  line-height: 1;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;

  && svg {
    font-size: 18px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const CopyButtonIcon = styled(EditButtonIcon)``;

const StyledEditIcon = styled(EditIcon)`
  font-size: 18px;
  color: ${({ theme }) => theme.action_icon_color};
  transition: color .15s ease;
`;

const StyledCopyIcon = styled(ContentCopyIcon)`
  font-size: 18px;
  color: ${({ theme }) => theme.action_icon_color};
  transition: color .15s ease;
`;

const StyledCheckIcon = styled(CheckCircleRoundedIcon)`
  font-size: 18px;
  color: ${({ theme }) => theme.action_icon_color};
`;

const OcrActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: transparent;
  border: none;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ theme }) => theme.action_icon_color};
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.font_color};
    transform: translateX(2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const OcrActionIcon = styled(SearchIcon)`
  font-size: 1rem;
  color: inherit;
`;

// File attachment styled components
const FileContainer = styled.span`
  display: flex;
  background-color: transparent;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  margin-right: 0;
  width: var(--thumb-size, 160px);
  flex: 0 0 var(--thumb-size, 160px);
`;

const FileThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const HistoryFilesContainer = styled.div<{ fileCount: number; $isMobile?: boolean; $stacked: boolean }>`
  --thumb-size: ${({ $isMobile }) => ($isMobile ? '140px' : '200px')};
  --thumb-height: ${({ $isMobile }) => ($isMobile ? '190px' : '270px')};
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'grid')};
  grid-template-columns: ${({ $stacked, fileCount }) => {
    if ($stacked) {
      return 'none';
    }
    const columns = Math.min(Math.max(fileCount, 1), 2);
    return `repeat(${columns}, var(--thumb-size))`;
  }};
  grid-auto-rows: ${({ $stacked }) => ($stacked ? 'auto' : 'var(--thumb-height)')};
  gap: ${({ $stacked }) => ($stacked ? '8px' : '12px')};
  justify-content: flex-start;
  align-items: flex-start;
  justify-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'start')};
  margin: 6px 0 0;
  width: 100%;
  max-width: ${({ $stacked, $isMobile }) => {
    if ($stacked) {
      return '100%';
    }
    if ($isMobile) {
      return '100%';
    }
    return 'calc(2 * var(--thumb-size) + 12px)';
  }};
  overflow: ${({ $stacked }) => ($stacked ? 'hidden auto' : 'visible')};

  ${({ $stacked }) =>
    $stacked && `
      flex-direction: column;
      max-height: 360px;
    `}
`;

const MessageFileContainer = styled(FileContainer)<{ $stacked: boolean }>`
  background-color: transparent;
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'block')};
  justify-content: ${({ $stacked }) => ($stacked ? 'flex-start' : 'center')};
  align-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'center')};
  margin-top: 6px;
  width: ${({ $stacked }) => ($stacked ? '100%' : 'var(--thumb-size, 160px)')};
  max-width: ${({ $stacked }) => ($stacked ? '100%' : 'var(--thumb-size, 160px)')};
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  margin-right: 0;
`;

const MessageFileRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: transparent;
  transition: background 0.12s ease, transform 0.08s ease;
  width: 100%;
  height: 100%;

  ${({ $clickable, theme }) => $clickable && `
    &:hover {
      background: ${theme.hover_overlay_light};
      transform: translateY(-1px);
    }
  `}
`;

const MessageFileName = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.font_color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
`;

const MessageFileThumb = styled(FileThumb)`
  width: var(--thumb-size, 200px);
  height: var(--thumb-height, var(--thumb-size, 200px));
  max-width: 100%;
  object-fit: cover;
`;

const MessageFilePlaceholder = styled.div<{ $variant: PlaceholderVariant }>`
  width: var(--thumb-size, 200px);
  height: var(--thumb-height, var(--thumb-size, 200px));
  max-width: 100%;
  border-radius: 16px;
  border: 1px solid ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).border};
  background: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).cardBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 24px 20px;
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).nameColor};
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 12px 26px rgba(0, 0, 0, 0.35)'
    : '0 12px 26px rgba(15, 23, 42, 0.08)'};
`;

const PlaceholderBadge = styled.div<{ $variant: PlaceholderVariant }>`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).iconBg};
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).iconColor};
`;

const PlaceholderDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const PlaceholderFilename = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  max-width: calc(var(--thumb-size, 200px) - 32px);
  word-break: break-word;
  white-space: normal;
`;

const PlaceholderFileType = styled.span<{ $variant: PlaceholderVariant }>`
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).typeColor};
`;

const ThumbWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: var(--thumb-size, 160px);
  height: var(--thumb-height, var(--thumb-size, 160px));
  align-items: center;
  justify-content: center;
`;

const HistoryThumbInner = styled.div<{ $clickable: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  outline: none;

  ${({ $clickable, theme }) => $clickable && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.mode === 'dark'
        ? '0 18px 34px rgba(0, 0, 0, 0.45)'
        : '0 14px 28px rgba(15, 23, 42, 0.12)'};
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.45);
    }
  `}
`;

const PageCountBadge = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  line-height: 1;
  color: white;
  background: ${({ theme }) => theme.file_page_badge_bg};
  border: 1px solid ${({ theme }) => theme.file_page_badge_border};
  border-radius: 999px;
  pointer-events: none;
`;

const CondensedAttachmentIcon = styled.div<{ $variant: PlaceholderVariant }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconBg};
  color: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconColor};
`;

// Bot message styled components
// Selo das mensagens entregues por uma tarefa agendada (não houve turno do
// usuário antes delas); o clique abre a tarefa na página Tarefas agendadas.
const ScheduledTaskTag = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  margin-bottom: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 550;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  background: ${({ theme }) => theme.hover_overlay_light};
  &:hover { color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 0.95rem; }
  & span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const BotMessageStrip = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  justify-content: flex-start;
  padding: 10px;
  position: relative;
  /* Skip layout/paint for off-screen messages -> fast scroll in long chats. */
  content-visibility: auto;
  contain-intrinsic-size: auto 320px;

  /* Accessible default: actions stay available when the primary input cannot
     hover reliably. Enhance only precise mouse/trackpad environments. */
  .assistant-action-button {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 120ms ease;
  }

  @media (hover: hover) and (pointer: fine) {
    .assistant-action-button {
      opacity: 0;
      pointer-events: none;
    }

    &:hover .assistant-action-button,
    &:focus-within .assistant-action-button,
    &.actions-visible .assistant-action-button,
    &.copy-always-visible .assistant-action-button {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const MessageStripContentArea = styled.div`
  display: flex;
  justify-content: flex-start;
  color: ${({theme})=> theme.font_color};
  width: 100%;
  max-width: 100%;
  margin: 0px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ActionIconButton = styled(IconButton)`
  && {
    color: ${({ theme }) => theme.action_icon_color};
    width: 30px;
    height: 30px;
    padding: 5px;
  }

  && svg {
    font-size: 18px;
  }
`;

const MoreMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const MoreMenu = styled.div`
  position: absolute;
  z-index: 1200;
  bottom: calc(100% + 4px);
  left: 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  background-color: ${({ theme }) => theme.sidebar_background_solid};
  min-width: 180px;
  white-space: nowrap;
  overflow: visible;
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 4px 12px rgba(0,0,0,0.4)'
    : '0 4px 12px rgba(0,0,0,0.1)'};
`;

const MoreMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const MoreMenuIcon = styled.span`
  display: flex;
  align-items: center;
  opacity: 0.6;
  font-size: 1.1rem;
`;

const MobileBottomSpacer = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
    height: 80px;
    flex-shrink: 0;
  }
`;

const MoreMenuComponentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
  & .assistant-action-button {
    opacity: 1 !important;
    pointer-events: auto !important;
    width: auto;
    height: auto;
    padding: 0;
    min-width: 0;
  }
  & .assistant-action-button svg {
    font-size: 1.1rem;
    opacity: 0.6;
  }
`;

const EndOfChatMessageMarker = styled.div`
  opacity: 0;
  height: 200px;
`;

// Botão "Continuar" após geração interrompida pelo limite de ciclos — mesmo
// formato dos botões do ErrorMessage, na cor de ação (verde do "Ver planos").
const ContinueStrip = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  padding: 0 20px 10px;
`;

const ContinueButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.error_plans_button_bg};
  color: #ffffff;
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  white-space: nowrap;
  font-family: 'Noka', sans-serif;

  &:hover {
    background-color: ${props => props.theme.error_plans_button_hover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Placeholder icons
const PdfPlaceholderIcon = MUIStyled(PictureAsPdfIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const ImagePlaceholderIcon = MUIStyled(InsertPhotoIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const GenericPlaceholderIcon = MUIStyled(InsertDriveFileIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const TextPlaceholderIcon = MUIStyled(ArticleIcon)`
  font-size: 2rem;
  color: currentColor;
`;

// --- Download response utilities ---

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadAsMarkdown(content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, 'resposta.md');
}

function markdownToSimpleHtml(md: string): string {
  let html = md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^[-*_]{3,}$/gm, '<hr>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.+?)\*/g, '<i>$1</i>')
    .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-family:monospace">$1</code>')
    .replace(/^\- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
  // Convert remaining lines to paragraphs
  html = html.split('\n').map(line => {
    if (line.startsWith('<h') || line.startsWith('<ul') || line.startsWith('<li') || line.startsWith('<hr') || line.trim() === '') return line;
    return `<p>${line}</p>`;
  }).join('\n');

  return html;
}

/**
 * Rasteriza um <img> já carregado para data URI. As imagens de conteúdo do
 * chat (gráficos matplotlib, imagens geradas) vivem em blob: URLs same-origin
 * — o servidor de export não consegue buscá-las, mas o canvas consegue lê-las
 * aqui e o data URI viaja dentro do próprio HTML/payload.
 */
function imgToDataUri(img: HTMLImageElement): string | null {
  if (!img.complete || !img.naturalWidth) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null; // cross-origin tainted — mantém o src original
  }
}

/** Imagens de CONTEÚDO da mensagem (gráfico/figura), não ícones de citação. */
function isContentImage(img: HTMLImageElement): boolean {
  const src = img.getAttribute('src') || '';
  if (src.includes('favicon')) return false;
  if (img.closest('.katex')) return false;
  // Imagens dentro de blocos de processo (tool calls, thinking) não são
  // conteúdo da resposta — o clone as remove do PDF; o DOCX também não embute.
  if (img.closest('[data-download-exclude]')) return false;
  return img.naturalWidth >= 64 && img.naturalHeight >= 64;
}

/**
 * Clones the rendered message DOM, converts <canvas> (Chart.js) to <img>,
 * and strips action buttons / ref buttons so only content remains.
 */
function cloneRenderedContent(buttonEl: HTMLElement): string {
  // Walk up to the MessageStripTextArea container
  const messageArea = buttonEl.closest('[data-message-content]');
  if (!messageArea) return '';

  const clone = messageArea.cloneNode(true) as HTMLElement;

  // Inline content <img>s as data URIs BEFORE any mutation (a troca de canvas
  // abaixo insere novos <img> no clone e quebraria o pareamento por índice).
  // Sem isso o PDF sai sem os gráficos: o src é um blob: URL local do browser
  // que o weasyprint do servidor não alcança.
  const originalImgs = messageArea.querySelectorAll('img');
  const clonedImgs = clone.querySelectorAll('img');
  clonedImgs.forEach((clonedImg, i) => {
    const originalImg = originalImgs[i] as HTMLImageElement;
    if (!originalImg || !isContentImage(originalImg)) return;
    const dataUri = imgToDataUri(originalImg);
    if (dataUri) (clonedImg as HTMLImageElement).src = dataUri;
  });

  // Convert all <canvas> to <img> (Chart.js charts)
  const originalCanvases = messageArea.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');
  clonedCanvases.forEach((clonedCanvas, i) => {
    const originalCanvas = originalCanvases[i] as HTMLCanvasElement;
    if (originalCanvas) {
      const img = document.createElement('img');
      try {
        img.src = originalCanvas.toDataURL('image/png');
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      } catch {
        // canvas tainted — skip
      }
      clonedCanvas.parentNode?.replaceChild(img, clonedCanvas);
    }
  });

  // Remove action buttons, ref buttons, toolbars, and standalone buttons
  // Preserve SVGs inside .katex elements (math equations)
  clone.querySelectorAll('[data-download-exclude], button').forEach(el => el.remove());
  clone.querySelectorAll('svg').forEach(el => {
    if (!el.closest('.katex')) el.remove();
  });

  return clone.innerHTML;
}


function extractChartImages(buttonEl: HTMLElement): string[] {
  const images: string[] = [];
  const messageArea = buttonEl.closest('[data-message-content]');
  if (!messageArea) return images;
  // Ordem do documento: canvases legados (Chart.js) e <img>s de conteúdo
  // (gráficos matplotlib/file blocks). O backend consome essa lista na mesma
  // ordem em que as imagens aparecem no markdown (```chart e ![...](...)).
  messageArea.querySelectorAll('canvas, img').forEach((el) => {
    if (el instanceof HTMLCanvasElement) {
      try {
        images.push(el.toDataURL('image/png'));
      } catch { /* canvas tainted */ }
    } else if (el instanceof HTMLImageElement && isContentImage(el)) {
      const dataUri = imgToDataUri(el);
      if (dataUri) images.push(dataUri);
    }
  });
  return images;
}

async function downloadViaBackend(buttonEl: HTMLElement, content: string, format: 'pdf' | 'docx', getToken: () => Promise<string>) {
  const renderedHtml = cloneRenderedContent(buttonEl);
  const bodyHtml = renderedHtml || markdownToSimpleHtml(content);
  const chartImages = extractChartImages(buttonEl);
  const token = await getToken();
  const ext = format === 'pdf' ? 'pdf' : 'docx';

  const res = await fetch('/api/chat/export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Auth ${token}`,
    },
    body: JSON.stringify({ html: bodyHtml, markdown: content, chart_images: chartImages, format }),
  });

  if (!res.ok) {
    // Fallback to markdown download on error
    downloadAsMarkdown(content);
    return;
  }

  const blob = await res.blob();
  saveAs(blob, `resposta.${ext}`);
}

const DownloadResponseButton: React.FC<{ content: string }> = ({ content }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { getAccessTokenSilently } = useAuth0();
  const open = Boolean(anchorEl);

  const getButtonEl = () => wrapperRef.current as HTMLElement;
  const closeMenu = () => setAnchorEl(null);

  const handleBackendDownload = async (format: 'pdf' | 'docx') => {
    closeMenu();
    setLoading(true);
    try {
      await downloadViaBackend(getButtonEl(), content, format, getAccessTokenSilently);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DownloadButtonWrapper ref={wrapperRef}>
      <CustomTooltip placement="top" title={open || loading ? '' : 'Baixar resposta'}>
        <ActionIconButton
          aria-label="Baixar resposta"
          className="assistant-action-button"
          onClick={(event) => {
            event.stopPropagation();
            if (!loading) setAnchorEl((current) => current ? null : event.currentTarget);
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <FileDownloadOutlinedIcon />}
        </ActionIconButton>
      </CustomTooltip>
      <DownloadDropdown
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={8}
        disableScrollLock
        MenuListProps={{ 'aria-label': 'Formatos de download da resposta' }}
        onClick={(event) => event.stopPropagation()}
      >
        <DownloadDropdownItem onClick={() => { downloadAsMarkdown(content); closeMenu(); }}>
          Markdown (.md)
        </DownloadDropdownItem>
        <DownloadDropdownItem onClick={() => handleBackendDownload('docx')}>
          Word (.docx)
        </DownloadDropdownItem>
        <DownloadDropdownItem onClick={() => handleBackendDownload('pdf')}>
          PDF
        </DownloadDropdownItem>
      </DownloadDropdown>
    </DownloadButtonWrapper>
  );
};

const DownloadButtonWrapper = styled.div`
  display: inline-flex;
`;

const DownloadDropdown = styled(Menu)`
  && .MuiPaper-root {
    min-width: 140px;
    overflow: hidden;
    background: ${({ theme }) => theme.chart_bg || theme.base_color};
    border: 1px solid ${({ theme }) => theme.chart_border || theme.borderGrey};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }

  && .MuiList-root {
    padding: 0;
  }
`;

const DownloadDropdownItem = styled(MenuItem)`
  && {
    width: 100%;
    min-height: 0;
    padding: 8px 14px;
    cursor: pointer;
    font-family: 'Sora', 'Lexend', sans-serif;
    font-size: 0.8125rem;
    white-space: nowrap;
    color: ${({ theme }) => theme.font_color};
    transition: background 0.15s;
  }

  &&:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
  }

  && + && {
    border-top: 1px solid ${({ theme }) => theme.chart_border || theme.borderGrey};
  }
`;

const canStreamAudio = typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

type TtsState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended';

// Module-level: only one TTS instance plays at a time.
// When a new instance starts, it calls this to stop the previous one.
let activeStopFn: (() => void) | null = null;

/** Stop any TTS playback globally. Call when switching conversations. */
export const stopAllTts = () => {
  if (activeStopFn) {
    activeStopFn();
    activeStopFn = null;
  }
};

const TextToSpeechButton: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
  const [ttsState, setTtsState] = useState<TtsState>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const prevContentRef = useRef(content);

  // Reset TTS state when content changes (e.g. switching conversations)
  useEffect(() => {
    if (prevContentRef.current !== content) {
      prevContentRef.current = content;
      if (audioRef.current) {
        const src = audioRef.current.src;
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
        if (src) URL.revokeObjectURL(src);
      }
      mediaSourceRef.current = null;
      setTtsState('idle');
    }
  }, [content]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      mediaSourceRef.current = null;
    };
  }, []);

  const stop = () => {
    if (activeStopFn === stop) activeStopFn = null;
    if (audioRef.current) {
      const src = audioRef.current.src;
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
      if (src) URL.revokeObjectURL(src);
    }
    mediaSourceRef.current = null;
    setTtsState('idle');
  };

  const onEnded = () => setTtsState('ended');
  const onError = () => stop();

  const setupAudio = (audio: HTMLAudioElement) => {
    audioRef.current = audio;
    audio.onended = onEnded;
    audio.onerror = onError;
  };

  const playStreaming = async (res: Response) => {
    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    const url = URL.createObjectURL(mediaSource);
    const audio = new Audio(url);
    setupAudio(audio);

    await new Promise<void>((resolve, reject) => {
      mediaSource.addEventListener('sourceopen', async () => {
        try {
          const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          const reader = res.body!.getReader();
          let started = false;
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (sourceBuffer.updating) {
                await new Promise<void>(r => sourceBuffer.addEventListener('updateend', () => r(), { once: true }));
              }
              if (mediaSource.readyState === 'open') mediaSource.endOfStream();
              break;
            }
            if (sourceBuffer.updating) {
              await new Promise<void>(r => sourceBuffer.addEventListener('updateend', () => r(), { once: true }));
            }
            sourceBuffer.appendBuffer(value);
            if (!started) {
              started = true;
              audio.play().then(() => setTtsState('playing')).catch(() => {});
            }
          }
          resolve();
        } catch (e) {
          reject(e);
        }
      }, { once: true });
    });
  };

  const playBlob = async (res: Response) => {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    setupAudio(audio);
    await audio.play();
    setTtsState('playing');
  };

  const fetchAndPlay = async () => {
    // Stop any other TTS instance that's currently playing
    if (activeStopFn) activeStopFn();
    activeStopFn = stop;
    setTtsState('loading');
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Auth ${token}`,
        },
        body: JSON.stringify({ text: content.slice(0, 10000) }),
      });
      if (!res.ok) throw new Error('TTS request failed');

      if (canStreamAudio && res.body) {
        await playStreaming(res);
      } else {
        await playBlob(res);
      }
    } catch {
      stop();
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setTtsState('paused');
    }
  };

  const resume = () => {
    if (audioRef.current) {
      activeStopFn = stop;
      audioRef.current.play().then(
        () => setTtsState('playing'),
        () => stop()
      );
    }
  };

  const replay = () => {
    if (audioRef.current) {
      activeStopFn = stop;
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(
        () => setTtsState('playing'),
        () => stop()
      );
    }
  };

  if (ttsState === 'idle') {
    return (
      <CustomTooltip placement="top" title="Ouvir mensagem">
        <ActionIconButton className={className} onClick={fetchAndPlay}>
          <VolumeUpIcon />
        </ActionIconButton>
      </CustomTooltip>
    );
  }

  if (ttsState === 'loading') {
    return (
      <ActionIconButton className={className} disabled>
        <CircularProgress size={18} sx={{ color: 'inherit' }} />
      </ActionIconButton>
    );
  }

  if (ttsState === 'playing') {
    return (
      <>
        <CustomTooltip placement="top" title="Pausar">
          <ActionIconButton className={className} onClick={pause}>
            <PauseIcon />
          </ActionIconButton>
        </CustomTooltip>
        <CustomTooltip placement="top" title="Parar">
          <ActionIconButton className={className} onClick={stop}>
            <StopIcon />
          </ActionIconButton>
        </CustomTooltip>
      </>
    );
  }

  if (ttsState === 'paused') {
    return (
      <>
        <CustomTooltip placement="top" title="Continuar">
          <ActionIconButton className={className} onClick={resume}>
            <PlayArrowIcon />
          </ActionIconButton>
        </CustomTooltip>
        <CustomTooltip placement="top" title="Parar">
          <ActionIconButton className={className} onClick={stop}>
            <StopIcon />
          </ActionIconButton>
        </CustomTooltip>
      </>
    );
  }

  // ttsState === 'ended'
  return (
    <CustomTooltip placement="top" title="Ouvir novamente">
      <ActionIconButton className={className} onClick={replay}>
        <ReplayIcon />
      </ActionIconButton>
    </CustomTooltip>
  );
};

const getTextContent = (content: string | ContentItem[]): string => {
  if (typeof content === 'string') {
    return content;
  }
  return content
    .filter((item): item is TextContentItem => item.type === 'text')
    .map(item => item.text)
    .join('\n');
};

// Helper to extract file items from array content
const getFileItems = (content: string | ContentItem[]): FileContentItem[] => {
  if (typeof content === 'string') {
    return [];
  }
  return content.filter((item): item is FileContentItem => item.type === 'input_file');
};

// Helper to extract file_ref items from array content (new format)
const getFileRefItems = (content: string | ContentItem[]): FileRefContentItem[] => {
  if (typeof content === 'string') {
    return [];
  }
  return content.filter((item): item is FileRefContentItem => item.type === 'file_ref');
};

// Collapsible "Raciocínio" (thinking) block — shows the reasoning the model
// wrote in the no-op reasoning tool. Collapsed by default.
const ReasoningRow = styled.div`
  display: flex; flex-direction: column; margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;
const ReasoningHeader = styled.div`
  display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
  padding: 4px 8px 4px 0; cursor: pointer; user-select: none; border-radius: 6px;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem; font-weight: 500;
  &:hover { background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}; }
`;
const ReasoningBody = styled.div`
  margin: 2px 0 8px 22px; white-space: pre-wrap; word-break: break-word;
  font-size: 0.85rem; line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary || theme.font_color}; opacity: 0.85;
`;
// When REACT_APP_HIDE_THINKING is "true" (set at build time, e.g. in prod), the
// model's reasoning is NEVER shown to end users — the collapsible "Pensamento"
// block is replaced by a static, non-expandable "Pensando..." marker. Leave the
// flag unset in dev/debug builds to inspect the full reasoning.
const HIDE_THINKING = process.env.REACT_APP_HIDE_THINKING === 'true';

const ReasoningBlock: React.FC<{ reasoning: string }> = ({ reasoning }) => {
  const [open, setOpen] = useState(false);
  const { disclosureRef, toggleDisclosure } = useStableDisclosure(open, setOpen);
  if (!reasoning || !reasoning.trim()) return null;
  if (HIDE_THINKING) {
    return (
      <ReasoningRow ref={disclosureRef} data-download-exclude>
        <ReasoningHeader as="div" style={{ cursor: 'default' }}>
          <PsychologyIcon style={{ fontSize: 15, opacity: 0.85 }} />
          Pensando...
        </ReasoningHeader>
      </ReasoningRow>
    );
  }
  return (
    <ReasoningRow ref={disclosureRef} data-download-exclude>
      <ReasoningHeader role="button" aria-expanded={open} onClick={toggleDisclosure}>
        <PsychologyIcon style={{ fontSize: 15, opacity: 0.85, marginLeft: -3 }} />
        Pensamento
        {open ? <ExpandLessIcon style={{ fontSize: 16, opacity: 0.6 }} /> : <ExpandMoreIcon style={{ fontSize: 16, opacity: 0.6 }} />}
      </ReasoningHeader>
      {open && <ReasoningBody>{reasoning}</ReasoningBody>}
    </ReasoningRow>
  );
};

// Respostas retidas no modo compacto (texto curto ainda podia ser só uma frase
// antes de ferramentas): quando aparecem, entram com streaming simulado desde o
// início. Chave por chat|mensagem|item; sai do conjunto quando o replay termina.
const heldAnswers = new Set<string>();

// Renders a ContentItem[] (or string fallback) in generation order: text, then file, then text...
const renderContentInOrder = (
  rawContent: string | ContentItem[] | undefined,
  references: Reference[],
  formatAssistantMessage: (text: string, refs: Reference[], isStreaming?: boolean) => React.ReactNode,
  isStreaming?: boolean,
  hideToolItems?: boolean,
  messageInterrupted?: boolean,
  messageIndex?: number,
  // Há um card de lista de tarefas mais recente noutra mensagem: os parciais
  // desta somem (só os 100% concluídos ficam).
  todoSuperseded = false,
): React.ReactNode => {
  if (!rawContent) return null;
  if (typeof rawContent === 'string') {
    return rawContent ? formatAssistantMessage(rawContent, references, isStreaming) : null;
  }
  // Collapse every consecutive burst of reasoning/tool/code items — anywhere
  // in the message, not just before the answer — into ONE expandable
  // "Pensando…/Pensou" line: a pile of boxes between text segments reads as
  // noise. A single bare tool call stays inline, and visible answer text is
  // never absorbed. Deep research (hideToolItems) keeps its own layout.
  // Chat do Sabiá 5: texto entre ferramentas vai para o status e a resposta
  // curta fica retida até HOLD_ANSWER_CHARS (ver compactAgentContent).
  const compact = isCompactActivity() && !hideToolItems;
  const original = rawContent;
  const content = compact ? compactAgentContent(rawContent, !!isStreaming) : rawContent;
  const heldKey = (i: number) => `${getThinkingScope()}|m${messageIndex ?? 'x'}|${i}`;
  if (compact && content !== original) {
    content.forEach((it: any, i) => {
      const was = original[i] as any;
      if (it && it.type === 'text' && !it.text && was && was.type === 'text' && (was.text || '').trim()) {
        heldAnswers.add(heldKey(i));
      }
    });
  }
  const thinkingRuns = hideToolItems ? [] : computeThinkingRuns(content);
  const runByStart = new Map(thinkingRuns.map((r) => [r.startIndex, r]));
  const swallowedIdx = groupedIndexSet(thinkingRuns, content);
  // Modo compacto: o checklist entra no status (as tarefas viram a lista) em
  // vez de um card à parte — no grupo que vem antes dele (o card vai sempre
  // para o fim do conteúdo), ou no primeiro, se nenhum vier antes.
  let todoIdx = -1;
  if (compact) content.forEach((it: any, i) => { if (it && it.type === 'todo_list') todoIdx = i; });
  const todoRun = todoIdx < 0 ? undefined
    : [...thinkingRuns].reverse().find((r) => r.startIndex < todoIdx) || thinkingRuns[0];
  return (
    <>
      {/* Render every item in chronological order — including reasoning blocks
          in-place. This conveys SEQUENCE: text → [tool/reasoning box] → text →
          [new box]. (Reasoning is no longer hoisted to the top; a reasoning or
          tool burst that happens AFTER the model already wrote text shows in its
          own box below that text, matching how it streamed.) */}
      {content.map((item, i) => {
        const el = (() => {
        const thinkingRun = runByStart.get(i);
        if (thinkingRun) {
          if (compact) {
            return (
              <ActivityStatus
                key={`think-run-${thinkingRun.ordinal}`}
                persistKey={`m${messageIndex ?? 'x'}-r${thinkingRun.ordinal}`}
                items={thinkingRun.items}
                pending={thinkingRunPending(content, thinkingRun, !!isStreaming, !!messageInterrupted)}
                interrupted={!!messageInterrupted && !hasVisibleItemsAfter(content, thinkingRun.endIndex)}
                todo={thinkingRun === todoRun ? (content[todoIdx] as TodoListContentItem) : null}
              />
            );
          }
          return (
            <ThinkingGroup
              key={`think-run-${thinkingRun.ordinal}`}
              persistKey={`m${messageIndex ?? 'x'}-r${thinkingRun.ordinal}`}
              items={thinkingRun.items}
              pending={
                thinkingRunPending(content, thinkingRun, !!isStreaming, !!messageInterrupted)
              }
              interrupted={
                // Only the run that was live at the stop: nothing visible after it.
                !!messageInterrupted && !hasVisibleItemsAfter(content, thinkingRun.endIndex)
              }
            />
          );
        }
        if (swallowedIdx.has(i)) {
          return null; // rendered inside its ThinkingGroup above
        }
        if (hideToolItems && (item.type === 'tool_action' || item.type === 'code_execution' || item.type === 'search_results')) {
          return null;
        }
        if (item.type === 'text' && item.text) {
          // Keep the same component mounted when streaming starts or finishes.
          // Swapping PacedText for a fragment remounted the full markdown tree
          // and made the answer visibly flash at both boundaries.
          const key = heldKey(i);
          const replay = compact && heldAnswers.has(key);
          return (
            <PacedText
              key={i}
              text={item.text}
              streaming={!!isStreaming}
              revealFromStart={replay}
              onRevealed={replay ? () => heldAnswers.delete(key) : undefined}
              render={(visible) => formatAssistantMessage(visible, references, !!isStreaming)}
            />
          );
        }
        if (item.type === 'file_ref') {
          return (
            <GeneratedFilePreview key={i} fileRefs={[{
              file_id: item.file_id,
              title: item.title,
              file_type: item.file_type as FileRef['file_type'],
              mime_type: item.mime_type,
              editable: item.editable,
              blob_size_bytes: item.blob_size_bytes || 0,
              ...(item.preview_file_id ? { preview_file_id: item.preview_file_id } : {}),
            }]} />
          );
        }
        if (item.type === 'input_file') {
          return (
            <GeneratedFilePreview key={i} files={[{
              path: item.filename,
              mime_type: item.mime_type || 'application/octet-stream',
              size_bytes: item.size_bytes || 0,
              content_base64: item.file_data,
              ...(item.content ? { content: item.content } : {}),
              ...(item.preview_pdf_base64 ? { preview_pdf_base64: item.preview_pdf_base64 } : {}),
            }]} />
          );
        }
        if (item.type === 'image_generating') {
          return <ImageGeneratingSkeleton key={i} hasInput={(item as any).has_input} />;
        }
        if (item.type === 'message_composer') {
          return <MessageComposerPreview key={i} data={item} />;
        }
        if (item.type === 'panorama_confirmation') {
          return <PanoramaConfirmation key={i} data={item} />;
        }
        if (item.type === 'deep_research_confirmation') {
          return <DeepResearchConfirmation key={i} data={item} />;
        }
        if (item.type === 'panorama_debug') {
          return <PanoramaDebug key={i} data={item} />;
        }
        if (item.type === 'panorama_summary') {
          return <PanoramaProgress key={i} phase={(item as any).phase || 'done'} progress={1} nSearches={(item as any).nSearches} nDocs={(item as any).nDocs} nTheses={(item as any).nTheses} nWebSearches={(item as any).nWebSearches} inputTokens={(item as any).inputTokens} outputTokens={(item as any).outputTokens} />;
        }
        if (item.type === 'reasoning') {
          // Pesquisa aprofundada: o card de progresso já conta a história do
          // turno; o raciocínio não é exibido (nem ao vivo, nem no histórico).
          if (hideToolItems) return null;
          return <ReasoningBlock key={i} reasoning={(item as any).reasoning || ''} />;
        }
        if (item.type === 'tool_action') {
          return <ToolActionBlock key={i} data={item as ToolActionContentItem} />;
        }
        if (item.type === 'code_execution') {
          return <CodeExecutionBlock key={i} data={item as CodeExecutionContentItem} />;
        }
        if (item.type === 'search_results') {
          return <SearchResultsBlock key={i} data={item as SearchResultsContentItem} />;
        }
        if (item.type === 'map') {
          // Visual output (like charts) — never swallowed into thinking groups
          // nor hidden by hideToolItems.
          const mapItem = item as MapContentItem;
          return <MapBlock key={i} titulo={mapItem.titulo} markers={mapItem.markers} />;
        }
        if (item.type === 'artifact') {
          // Cartão do artefato: abre o painel lateral (fora dos grupos de raciocínio, como o mapa).
          return <ArtifactCard key={i} data={item as ArtifactContentItem} />;
        }
        if (item.type === 'activity_step') return null; // rótulo do status (ActivityStatus)
        if (item.type === 'todo_list') {
          // Checklist do modelo: fora dos grupos de raciocínio, como o mapa.
          if (todoRun && i === todoIdx) return null; // compacto: mostrado no status
          if (!shouldShowTodoCard(item as TodoListContentItem, todoSuperseded)) return null;
          return <TodoListBlock key={i} data={item as TodoListContentItem} live={!!isStreaming} />;
        }
        return null;
        })();
        if (el == null) return null;
        return (
          <ContentItemErrorBoundary key={i} itemType={(item as any)?.type} resetKeys={[!!isStreaming]}>
            {el}
          </ContentItemErrorBoundary>
        );
      })}
    </>
  );
};

// Empty-refs sentinel: an assistant message with no references keeps a STABLE
// prop reference across renders (a fresh [] each render would defeat React.memo).
const EMPTY_REFS: Reference[] = [];

// Memoized markdown rendering. The markdown parse (remark/rehype/KaTeX) is the
// dominant per-message cost in long conversations; wrapping it in React.memo lets
// UNCHANGED messages bail out so a keystroke/token no longer re-parses every
// message. Interactions (copy, like, menus, edit) stay OUTSIDE these components,
// so they keep re-rendering and updating normally — only the expensive markdown
// is skipped for messages whose content did not change.
const MemoUserMarkdown = React.memo(({ content }: { content: string | ContentItem[] }) => (
  <UserMessageMarkdown
    remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
    rehypePlugins={[rehypeKatex]}
    components={{ p: ({ children }) => <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{children}</p> }}
  >
    {preprocessingLatex(getMessageTextContent(content))}
  </UserMessageMarkdown>
));
MemoUserMarkdown.displayName = 'MemoUserMarkdown';

interface MemoAssistantContentProps {
  content: string | ContentItem[];
  references: Reference[];
  fmtRef: React.MutableRefObject<(text: string, refs?: Reference[], isStreaming?: boolean) => React.ReactNode>;
  isStreaming: boolean;
  hideToolItems: boolean;
  interrupted: boolean;
  messageIndex?: number;
  todoSuperseded?: boolean;
}
// The filtered messageRefs are derived from the (stable) references + content, so
// we compute them INSIDE the memo — passing the pre-filtered array as a prop would
// hand React.memo a fresh array every render and defeat the bail-out.
export const MemoAssistantContent = React.memo((p: MemoAssistantContentProps) => {
  const textContent = getTextContent(p.content);
  const messageRefs = p.references.filter((ref) => ref.link && textContent.includes(ref.link));
  return <>{renderContentInOrder(p.content, messageRefs, p.fmtRef.current as any, p.isStreaming, p.hideToolItems, p.interrupted, p.messageIndex, p.todoSuperseded)}</>;
});
MemoAssistantContent.displayName = 'MemoAssistantContent';

interface UserMessageRowProps {
  message: any;
  index: number;
  isLastOrSecondToLast: boolean;
  copied: boolean;
  isEditing: boolean;
  editingThis: boolean;
  editValue: string;
  editBusy: boolean;
  isMobile: boolean;
  numPages: Record<string, number> | null;
  filePreviews: Record<string, string>;
  fileTextPreviews: Record<string, { content: string; truncated: boolean }>;
  fileDownloadProgress: Record<string, number>;
  handlersRef: React.MutableRefObject<any>;
}

// A whole user message row, memoized (same rationale as AssistantMessageRow).
const UserMessageRow = React.memo((props: UserMessageRowProps) => {
  const { message, index, isLastOrSecondToLast, copied, isEditing, editingThis, editValue, editBusy, isMobile, numPages, filePreviews, fileTextPreviews, fileDownloadProgress } = props;
  const h = props.handlersRef;
  return (
    <UserMessageStrip key={index} data-testid="user-message-row" data-message-index={index}>
              <UserMessageStack>
                <UserMessageStripContentArea
                  style={{ width: isEditing && isLastOrSecondToLast ? "100%" : "auto", maxWidth: isEditing && isLastOrSecondToLast ? "100%" : "80%" }}
                >
                  <UserMessageStripTextArea>
                    {editingThis ? (
                      <form onSubmit={h.current.handleSaveEditedMessage}>
                        <EditTextArea
                          id={`edit-textarea-${index}`}
                          value={editValue}
                          onChange={h.current.handleEditMessageInputChange}
                        />
                        <EditButtons>
                          <EditButton type="submit" disabled={(editBusy) || editValue.trim().length == 0}>Enviar</EditButton>
                          <EditButton type="button" onClick={h.current.handleCancelEdit}>Cancelar</EditButton>
                        </EditButtons>
                      </form>
                    ) : (
                      <CollapsibleUserMessage>
                        <MemoUserMarkdown content={message.content} />
                      </CollapsibleUserMessage>
                    )}
                    {!isEditing && message.filenames && message.filenames.length > 0 && (
                      (() => {
                        const fileCountForLayout = message.filenames.length;
                        const shouldStackAttachments = fileCountForLayout > 6;
                        const showGridPreviews = !shouldStackAttachments;

                        return (
                          <HistoryFilesContainer
                            fileCount={fileCountForLayout}
                            $isMobile={isMobile}
                            $stacked={shouldStackAttachments}
                          >
                            {message.filenames.map((filename: string, fileIdx: number) => {
                              const variant = getPlaceholderVariant(filename);
                              const pageCount = numPages?.[filename];
                              const previewUrl = filePreviews[filename];
                              const _lowerFn = filename.toLowerCase();
                              const _isOfficeFile = _lowerFn.endsWith('.docx') || _lowerFn.endsWith('.xlsx') || _lowerFn.endsWith('.rtf');
                              const canPreview = Boolean(previewUrl) || Boolean(fileTextPreviews[filename]) || isPdfFile(filename) || isImageFile(filename) || _isOfficeFile;
                              const showPageCountFlag = shouldShowPageCount(filename, pageCount);
                              const pageCountLabel = showPageCountFlag && typeof pageCount === 'number'
                                ? formatPageCountLabel(pageCount)
                                : '';

                              if (showGridPreviews) {
                                const placeholderIcon = (() => {
                                  if (variant === 'pdf') return <PdfPlaceholderIcon />;
                                  if (variant === 'image') return <ImagePlaceholderIcon />;
                                  if (variant === 'textCsv' || variant === 'textTxt') return <TextPlaceholderIcon />;
                                  return <GenericPlaceholderIcon />;
                                })();

                                const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
                                  if (!canPreview) {
                                    return;
                                  }
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    h.current.handleOpenPreview(filename);
                                  }
                                };

                                return (
                                  <MessageFileContainer key={`${filename}-${fileIdx}`} $stacked={false}>
                                    <CustomTooltip title={filename} placement="top">
                                      <ThumbWrapper>
                                        {(() => {
                                          const dlProgress = fileDownloadProgress[filename];
                                          const isDownloading = dlProgress !== undefined && dlProgress < 100;
                                          return isDownloading ? (
                                            <HistoryThumbInner $clickable={false}>
                                              <div style={{
                                                width: '100%', height: '100%',
                                                display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center',
                                                gap: 6,
                                              }}>
                                                <div style={{ position: 'relative', display: 'inline-flex' }}>
                                                  <CircularProgress
                                                    variant="determinate"
                                                    value={dlProgress}
                                                    size={44}
                                                    thickness={4}
                                                  />
                                                  <div style={{
                                                    position: 'absolute', inset: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  }}>
                                                    <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.8 }}>
                                                      {dlProgress}%
                                                    </span>
                                                  </div>
                                                </div>
                                                <span style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }} title={filename}>
                                                  {filename}
                                                </span>
                                              </div>
                                            </HistoryThumbInner>
                                          ) : (
                                            <HistoryThumbInner
                                              $clickable={canPreview}
                                              role={canPreview ? 'button' : undefined}
                                              tabIndex={canPreview ? 0 : -1}
                                              onClick={canPreview ? () => h.current.handleOpenPreview(filename) : undefined}
                                              onKeyDown={handleKeyDown}
                                            >
                                              {previewUrl ? (
                                                <MessageFileThumb src={previewUrl} alt={filename} />
                                              ) : (
                                                <MessageFilePlaceholder $variant={variant}>
                                                  <PlaceholderBadge $variant={variant}>
                                                    {placeholderIcon}
                                                  </PlaceholderBadge>
                                                  <PlaceholderDetails>
                                                    <PlaceholderFilename title={filename}>{filename}</PlaceholderFilename>
                                                    <PlaceholderFileType $variant={variant}>
                                                      {placeholderTypeLabels[variant]}
                                                    </PlaceholderFileType>
                                                  </PlaceholderDetails>
                                                </MessageFilePlaceholder>
                                              )}
                                            </HistoryThumbInner>
                                          );
                                        })()}
                                        {pageCountLabel && (
                                          <PageCountBadge>
                                            {pageCountLabel}
                                          </PageCountBadge>
                                        )}
                                      </ThumbWrapper>
                                    </CustomTooltip>
                                  </MessageFileContainer>
                                );
                              }

                              const pageInfo = pageCountLabel
                                ? ` • ${pageCountLabel}`
                                : '';

                              const rowPreviewIcon = getVariantIcon(variant);

                              {
                                const dlProgress = fileDownloadProgress[filename];
                                const isDownloading = dlProgress !== undefined && dlProgress < 100;
                                return (
                                  <MessageFileContainer key={`${filename}-${fileIdx}`} $stacked={true}>
                                    <MessageFileRow
                                      role={canPreview && !isDownloading ? 'button' : undefined}
                                      onClick={canPreview && !isDownloading ? () => h.current.handleOpenPreview(filename) : undefined}
                                      $clickable={canPreview && !isDownloading}
                                    >
                                      {isDownloading ? (
                                        <CircularProgress
                                          variant="determinate"
                                          value={dlProgress}
                                          size={18}
                                          thickness={5}
                                          sx={{ color: 'inherit', marginRight: '8px', flexShrink: 0 }}
                                        />
                                      ) : (
                                        <CondensedAttachmentIcon $variant={variant}>
                                          {rowPreviewIcon}
                                        </CondensedAttachmentIcon>
                                      )}
                                      <MessageFileName title={filename}>
                                        {filename}{isDownloading ? ` ${dlProgress}%` : pageInfo}
                                      </MessageFileName>
                                    </MessageFileRow>
                                  </MessageFileContainer>
                                );
                              }
                            })}
                          </HistoryFilesContainer>
                        );
                      })()
                    )}
                  </UserMessageStripTextArea>
                </UserMessageStripContentArea>
                {!isEditing && (
                  <UserMessageActions className="user-message-actions">
                    <CustomTooltip
                      title={copied ? 'Copiado!' : 'Copiar mensagem'}
                      placement="top"
                    >
                      <CopyButtonIcon
                        type="button"
                        onClick={() => h.current.handleCopyToClipboard(getMessageTextContent(message.content), index, message.references as Reference[] | undefined)}
                      >
                        {copied ? <StyledCheckIcon /> : <StyledCopyIcon />}
                      </CopyButtonIcon>
                    </CustomTooltip>
                    {isLastOrSecondToLast && (
                      <CustomTooltip title="Editar mensagem" placement="top">
                        <EditButtonIcon onClick={() => h.current.handleEditMessage(index, getMessageTextContent(message.content))}>
                          <StyledEditIcon />
                        </EditButtonIcon>
                      </CustomTooltip>
                    )}
                  </UserMessageActions>
                )}
              </UserMessageStack>
            </UserMessageStrip>
  );
});
UserMessageRow.displayName = 'UserMessageRow';

interface AssistantMessageRowProps {
  message: any;
  index: number;
  isLast: boolean;
  todoSuperseded?: boolean;
  copied: boolean;
  menuOpen: boolean;
  hideActionButtons: boolean;
  showRegenerate: boolean;
  isStreamingThis: boolean;
  drActive: boolean;
  loadingGeneration: boolean;
  loadingMessage: string;
  showInlineDebug: boolean;
  currentToolCalls: ToolCallEntry[];
  /** Card de progresso AO VIVO (panorama / pesquisa aprofundada) do turno
   * corrente — renderizado DENTRO da mensagem, no mesmo lugar em que o card
   * persistido fica depois (sem o texto "subir" para cima do card no fim). */
  liveProgress: React.ReactNode;
  handlersRef: React.MutableRefObject<any>;
  fmtRef: React.MutableRefObject<any>;
}

// A whole assistant message row, memoized. All props are values/booleans/stable
// refs, so the default shallow compare bails for every row whose own state did not
// change — a keystroke or a streamed token no longer re-renders the other rows'
// action buttons / tooltips / markdown at all.
export const AssistantMessageRow = React.memo((props: AssistantMessageRowProps) => {
  const {
    message, index, isLast, todoSuperseded, copied, menuOpen, hideActionButtons,
    showRegenerate, isStreamingThis, drActive, loadingGeneration, loadingMessage,
    showInlineDebug, currentToolCalls, liveProgress,
  } = props;
  const h = props.handlersRef;
  const textContent = getTextContent(message.content);
  const hasFileOutput = Array.isArray(message.content)
    && message.content.some((it: any) => it && (it.type === 'file_ref' || it.type === 'input_file'));
  const fileItemsFromContent = getFileItems(message.content);
  const fileRefItemsFromContent = getFileRefItems(message.content);
  const hasToolItems = Array.isArray(message.content)
    && message.content.some((it: any) => it && (it.type === 'tool_action' || it.type === 'code_execution'));
  const hasReasoning = Array.isArray(message.content)
    && message.content.some((it: any) => it && it.type === 'reasoning');
  // A resumed compact stream can contain only activity steps until the final
  // history refresh restores its tools/files. Keep its live status visible —
  // only while the turn is live: steps are not persisted, so a finished turn
  // with nothing else would otherwise stay as a bare "Concluído · N etapas".
  // Live means THIS row (the last one): an older activity-only turn (e.g. one
  // that errored) reappearing above the new message while another turn loads
  // pushes the just-sent message off its aligned position.
  const hasActivitySteps = (!!isStreamingThis || (!!loadingGeneration && !!isLast))
    && Array.isArray(message.content)
    && message.content.some((it: any) => it && it.type === 'activity_step');
  const hasLiveActivityIndicator = Array.isArray(message.content) && (
    message.content.some((it: any) => it && it.pending)
    || (loadingGeneration && trailingThinkingRunActive(compactAgentContent(message.content, !!isStreamingThis)))
  );
  const drMeta = (message as any).deep_research_metadata;
  // Turno de panorama salvo: o card (panorama_summary) conta a história —
  // mesmo tratamento do turno de pesquisa aprofundada (sem Pensamento solto).
  const hasPanoramaSummary = Array.isArray(message.content)
    && (message.content as any[]).some((it) => it && it.type === 'panorama_summary');
  const hideToolItems = !!drMeta || hasPanoramaSummary || (isLast && drActive);
  // Hidden reasoning must not mount an empty strip: its padding and deferred
  // intrinsic height otherwise move the progress card as streaming starts.
  const hasContent = textContent !== "" || fileItemsFromContent.length > 0 || fileRefItemsFromContent.length > 0
    || (!hideToolItems && (hasToolItems || hasReasoning || hasActivitySteps)) || hasPanoramaSummary || !!drMeta?.plan || !!liveProgress
    || hasVisibleTodoCard(message.content, !!todoSuperseded);
  if (!hasContent) return null;
  const allRefs = (message.references as Reference[]) || EMPTY_REFS;
  return (
    <BotMessageStrip className={menuOpen ? 'actions-visible' : ''}>
      <MessageStripContentArea>
        <MessageStripTextArea
          data-message-content
          style={{
            // Mensagem de pesquisa/panorama: o card ocupa a coluna inteira
            // também no histórico (a área encolhe para o texto; um relatório
            // curto deixava o card mais estreito do que ao vivo).
            ...((drMeta && drMeta.plan) || hasPanoramaSummary || liveProgress ? { width: '100%' } : {}),
          }}
        >
          {(message as any).scheduled_task && (
            <ScheduledTaskTag
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openTasksView', {
                detail: { taskId: (message as any).scheduled_task.task_id },
              }))}
              title="Ver tarefa agendada"
            >
              <ScheduleIcon />
              <span>Tarefa agendada{(message as any).scheduled_task.title ? ` · ${(message as any).scheduled_task.title}` : ''}</span>
            </ScheduledTaskTag>
          )}
          {liveProgress}
          {drMeta && drMeta.plan && (
            <DeepResearchProgress
              phase={drMeta.complete === false ? "interrupted" : "done"}
              progress={drMeta.complete === false ? 0 : 1}
              steps={drMeta.step_results || {}}
              inputTokens={drMeta.input_tokens || 0}
              outputTokens={drMeta.output_tokens || 0}
            />
          )}
          <MemoAssistantContent
            content={message.content}
            references={(message.references as Reference[]) || EMPTY_REFS}
            fmtRef={props.fmtRef}
            messageIndex={index}
            todoSuperseded={todoSuperseded}
            isStreaming={isStreamingThis}
            hideToolItems={hideToolItems}
            interrupted={!!(message as any).interrupted}
          />
          {isStreamingThis && loadingGeneration && !hideToolItems && !hasLiveActivityIndicator && (
            <LoadingMessageExtracted loadingMessage={loadingMessage} />
          )}
          {showInlineDebug && (
            <ToolCallsDebugPanel toolCalls={currentToolCalls} isStreaming={loadingGeneration} />
          )}
          {typeof message.content === 'string' && message.generated_files && message.generated_files.length > 0 && (() => {
            const genFiles = message.generated_files as (GeneratedFile | FileRef)[];
            return (
              <GeneratedFilePreview
                files={genFiles.filter((f): f is GeneratedFile => 'content_base64' in f)}
                fileRefs={genFiles.filter((f): f is FileRef => 'file_id' in f)}
              />
            );
          })()}
          {/* Turno que não chegou a responder (só o status, ex.: erro no meio):
              copiar/gostei não têm o que agir, e a barra afastava o erro do status. */}
          {/* Escondidos durante a geração SEM sair do layout: no envio, a
              resposta anterior ainda é a última por um instante e encolher a
              barra (~45px) deslocava a mensagem recém-enviada do topo. */}
          {(!!textContent.trim() || hasFileOutput) && (
            <ActionButtonsContainer
              data-download-exclude
              data-testid="assistant-actions"
              aria-hidden={hideActionButtons || undefined}
              style={hideActionButtons ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
            >
              <CustomTooltip placement="top" title={copied ? 'Copiado!' : 'Copiar mensagem'}>
                <ActionIconButton className="assistant-action-button copy-icon" onClick={() => h.current.handleCopyToClipboard(textContent, index, allRefs)}>
                  {copied ? <CheckCircleRoundedIcon /> : <ContentCopyIcon />}
                </ActionIconButton>
              </CustomTooltip>
              {showRegenerate && (
                <CustomTooltip placement="top" title="Gerar nova resposta">
                  <ActionIconButton className="assistant-action-button regenerate-icon" onClick={() => h.current.handleRegenerateMessage(index - 1)}>
                    <ReplayIcon />
                  </ActionIconButton>
                </CustomTooltip>
              )}
              <CustomTooltip placement="top" title={message.feedback === 'like' ? 'Remover gostei' : 'Gostei da resposta'}>
                <ActionIconButton className="assistant-action-button like-icon" onClick={() => h.current.handleLikeMessage(index)}>
                  <ThumbUpOutlinedIcon sx={{ color: message.feedback === 'like' ? '#4f46e5' : 'inherit' }} />
                </ActionIconButton>
              </CustomTooltip>
              <CustomTooltip placement="top" title={message.feedback === 'dislike' ? 'Remover feedback' : 'Não gostei da resposta'}>
                <ActionIconButton className="assistant-action-button dislike-icon" onClick={(e) => h.current.handleDislikeClick(e, index)}>
                  <ThumbDownOutlinedIcon sx={{ color: message.feedback === 'dislike' ? 'error.main' : 'inherit' }} />
                </ActionIconButton>
              </CustomTooltip>
              <TextToSpeechButton content={textContent} className="assistant-action-button" />
              <MoreMenuWrapper>
                <ActionIconButton className="assistant-action-button" onClick={(e) => { e.stopPropagation(); h.current.setMoreMenuIndex(menuOpen ? null : index); }}>
                  <MoreHorizIcon />
                </ActionIconButton>
                {menuOpen && (
                  <MoreMenu onClick={(e) => e.stopPropagation()}>
                    <MoreMenuItem onClick={() => { h.current.handleShareSingleMessage(index); h.current.setMoreMenuIndex(null); }}>
                      <MoreMenuIcon><IosShareIcon style={{ fontSize: '1.1rem' }} /></MoreMenuIcon>
                      Compartilhar
                    </MoreMenuItem>
                    <MoreMenuComponentRow onClick={(e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest('button')) { const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); } }}>
                      <DownloadResponseButton content={textContent} />
                      Baixar resposta
                    </MoreMenuComponentRow>
                  </MoreMenu>
                )}
              </MoreMenuWrapper>
            </ActionButtonsContainer>
          )}
        </MessageStripTextArea>
      </MessageStripContentArea>
    </BotMessageStrip>
  );
});
AssistantMessageRow.displayName = 'AssistantMessageRow';

const cmpBtnStyle: React.CSSProperties = {
  flex: '1 1 auto', padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
  border: '1px solid rgba(127,127,127,0.35)', background: 'transparent', color: 'inherit',
  font: 'inherit', fontWeight: 600,
};

// Friendly labels for the model reveal (post-pick). Falls back to the raw id.
const CMP_MODEL_LABELS: Record<string, string> = {
  'competidor-opus48': 'Opus 4.8',
  'competidor': 'Competidor (GPT-5.2)',
  'competidor-2': 'Competidor (Opus 4.6)',
  'competidor-3': 'Competidor (Qwen)',
  'sabia-4-thinking': 'Sabiá 4 (thinking)',
  'sabia-5': 'Sabiá 5',
  'sabia-4': 'Sabiá 4',
  'sabiazinho-4': 'Sabiazinho 4',
};
const cmpModelLabel = (m?: string) => (m && CMP_MODEL_LABELS[m]) || m || '';

// Blinded side-by-side comparison of two model responses for one turn, with the
// preference-annotation controls. The two columns reuse renderContentInOrder via
// the `renderSlot` prop, so text / reasoning / tool chips render like a normal
// assistant message. Manages only its own reason-textarea state.
const ComparisonBlock: React.FC<{
  comparison: ActiveComparison;
  onSubmit: (preferred: 'left' | 'right' | 'tie', reason?: string) => void;
  onFinish: () => void;
  renderSlot: (slot: ComparisonSlot, isLeft: boolean) => React.ReactNode;
}> = ({ comparison, onSubmit, onFinish, renderSlot }) => {
  const [reason, setReason] = React.useState('');
  const bothDone = comparison.left.done && comparison.right.done;
  const submitting = !!comparison.submitting;
  const revealed = !!comparison.revealed;
  const pick = (p: 'left' | 'right' | 'tie') => { if (!submitting && bothDone && !revealed) onSubmit(p, reason.trim() || undefined); };
  const committedSlot = comparison.commitSlot === 'right' ? comparison.right : comparison.left;
  return (
    <BotMessageStrip className="copy-always-visible">
      <MessageStripContentArea>
        <MessageStripTextArea data-message-content>
          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: 8 }}>
            {revealed ? 'Modo comparação — resultado (modelos revelados)' : 'Modo comparação — escolha a melhor resposta (as respostas estão anonimizadas)'}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {(['left', 'right'] as const).map((side, i) => {
              const slot = side === 'left' ? comparison.left : comparison.right;
              const isWinner = revealed && comparison.preferred === side;
              return (
                <div key={side} style={{ flex: '1 1 320px', minWidth: 280, border: `1px solid ${isWinner ? '#4f46e5' : 'rgba(127,127,127,0.3)'}`, borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, opacity: 0.85 }}>
                    {`Resposta ${i === 0 ? 'A' : 'B'}`}
                    {revealed && <span style={{ fontWeight: 400, opacity: 0.75 }}> — {cmpModelLabel(slot.model)}</span>}
                    {isWinner && <span style={{ fontWeight: 600, color: '#4f46e5' }}> ✓ sua escolha</span>}
                    {!slot.done && !revealed && <span style={{ fontWeight: 400, opacity: 0.6 }}> • gerando…</span>}
                    {slot.error && <span style={{ fontWeight: 400, color: '#d33' }}> • erro</span>}
                  </div>
                  {renderSlot(slot, side === 'left')}
                </div>
              );
            })}
          </div>
          {bothDone && !revealed && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button disabled={submitting} onClick={() => pick('left')} style={cmpBtnStyle}>Prefiro A</button>
                <button disabled={submitting} onClick={() => pick('tie')} style={cmpBtnStyle}>Empate</button>
                <button disabled={submitting} onClick={() => pick('right')} style={cmpBtnStyle}>Prefiro B</button>
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Por que essa resposta é melhor? (opcional)"
                rows={2}
                disabled={submitting}
                style={{ marginTop: 8, width: '100%', boxSizing: 'border-box', borderRadius: 8, padding: 8, border: '1px solid rgba(127,127,127,0.3)', background: 'transparent', color: 'inherit', font: 'inherit', resize: 'vertical' }}
              />
              {submitting && <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 4 }}>Registrando…</div>}
            </div>
          )}
          {revealed && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                {comparison.preferred === 'tie'
                  ? `Empate registrado — seguindo com ${cmpModelLabel(committedSlot.model)}.`
                  : `Você escolheu ${cmpModelLabel(committedSlot.model)}.`}
              </span>
              <button onClick={onFinish} style={{ ...cmpBtnStyle, flex: '0 0 auto' }}>Continuar</button>
            </div>
          )}
        </MessageStripTextArea>
      </MessageStripContentArea>
    </BotMessageStrip>
  );
};

const MessageList: React.FC = () => {
  const {
    isMobile,
    messageHistory,
    messageBeingGenerated,
    loadingGeneration,
    loadingMessage,
    extractingFiles,
    chatErrorMessage,
    allowErrorRetry,
    showPlanUpgradeOption,
    isEditing,
    editingMessageId,
    messageBeingEdited,
    copiedIndex,
    planStatus,
    freeQuota,
    quotaBadgeIndex,
    filePreviews,
    fileTextPreviews,
    numPages,
    fileDownloadProgress,
    loadingOcrModal,
    activeChatID,
    handleEditMessage,
    handleSaveEditedMessage,
    handleCancelEdit,
    handleEditMessageInputChange,
    handleCopyToClipboard,
    handleLikeMessage,
    handleDislikeClick,
    handleRegenerateMessage,
    handleRetryMessage,
    handleContinueGeneration,
    queuedMessages,
    heldQueueChatIds,
    removeQueuedMessage,
    editQueuedMessage,
    sendQueuedNow,
    handleOpenPreview,
    handleOpenOcrModal,
    hasOcrContentForMessage,
    setOpenSubscriptionModal,
    formatAssistantMessage,
    getVisibleReferences,
    getCitedReferencesWithHistory,
    renderQuotaInfoTrigger,
    endOfChatMessageMarkerRef,
    deepResearchPhase,
    deepResearchProgress,
    deepResearchSteps,
    deepResearchTokens,
    deepResearchStartedAt,
    panoramaPhase,
    panoramaProgress,
    panoramaNSearches,
    panoramaNDocs,
    panoramaNTheses,
    panoramaWebSearches,
    panoramaInputTokens,
    panoramaOutputTokens,
    panoramaStartedAt,
    debugMode,
    currentToolCalls,
    activeComparison,
    submitComparisonAnnotation,
    finishComparison,
  } = usePlayground();

  // Scope the thinking-group persistence keys to the active chat BEFORE any
  // child renders (module-level var; render-body on purpose — effects would
  // run after the children already mounted with the stale scope).
  setThinkingScope(activeChatID?.chatID || '');
  const lastTodoIdx = lastTodoMessageIndex(messageHistory as any);
  // Todo mundo conversa com o Sabiá 5 (03/11/2026): status compacto + só a resposta.
  setCompactActivity(true);

  useRenderTracker('MessageList', () => ({
    messageHistory, messageBeingGenerated, loadingGeneration, loadingMessage,
    extractingFiles, chatErrorMessage, isEditing, editingMessageId, messageBeingEdited,
    copiedIndex, activeChatID, deepResearchPhase, deepResearchProgress, panoramaPhase,
    panoramaProgress, debugMode, currentToolCalls,
  }));

  // formatAssistantMessage is a plain const in Playground → recreated with a new
  // identity every render. Reach it through a ref so MemoAssistantContent can bail
  // on keystroke without stale closures (ref.current is always the latest).
  const fmtRef = useRef(formatAssistantMessage);
  fmtRef.current = formatAssistantMessage;

  const [moreMenuIndex, setMoreMenuIndex] = useState<number | null>(null);
  useEffect(() => {
    if (moreMenuIndex === null) return;
    const handler = () => setMoreMenuIndex(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [moreMenuIndex]);

  // Per-message share state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareMessages, setShareMessages] = useState<{role: 'user' | 'assistant'; content: string | ContentItem[]}[] | undefined>(undefined);
  const [shareMessageIndex, setShareMessageIndex] = useState<number | undefined>(undefined);

  const handleShareSingleMessage = (assistantIndex: number) => {
    const assistantMsg = messageHistory[assistantIndex];
    // Preserve full content (including file_ref items for charts/images)
    const msgs: {role: 'user' | 'assistant'; content: string | ContentItem[]; references?: any[]}[] = [
      { role: 'assistant', content: assistantMsg.content, references: (assistantMsg as any).references }
    ];
    setShareMessages(msgs);
    setShareMessageIndex(assistantIndex);
    setShareModalOpen(true);
  };

  // Bundle the row's handlers behind a stable ref so AssistantMessageRow can be
  // memoized (unstable handler identities would otherwise defeat the bail-out).
  const handlersRef = useRef<any>({});
  handlersRef.current = {
    handleCopyToClipboard,
    handleRegenerateMessage,
    handleLikeMessage,
    handleDislikeClick,
    handleShareSingleMessage,
    setMoreMenuIndex,
    handleEditMessage,
    handleSaveEditedMessage,
    handleCancelEdit,
    handleEditMessageInputChange,
    handleOpenPreview,
  };

  // Card de progresso do turno corrente (panorama / pesquisa aprofundada).
  // Vai DENTRO da última mensagem do assistente, na mesma posição em que o
  // card persistido (panorama_summary / deep_research_metadata) fica ao final:
  // o texto do relatório é escrito abaixo dele e nada troca de lugar quando o
  // turno termina. Só cai para uma faixa própria quando ainda não há bolha do
  // assistente (ex.: retomada de um stream em segundo plano).
  const liveResearchCard: React.ReactNode = panoramaPhase && panoramaPhase !== 'done'
    ? <PanoramaProgress phase={panoramaPhase} progress={panoramaProgress} nSearches={panoramaNSearches} nDocs={panoramaNDocs} nTheses={panoramaNTheses} nWebSearches={panoramaWebSearches} inputTokens={panoramaInputTokens} outputTokens={panoramaOutputTokens} startedAt={panoramaStartedAt} />
    : deepResearchPhase && deepResearchPhase !== 'done'
    ? <DeepResearchProgress
        startedAt={deepResearchStartedAt}
        phase={deepResearchPhase}
        progress={deepResearchProgress}
        steps={deepResearchSteps}
        inputTokens={deepResearchTokens.input}
        outputTokens={deepResearchTokens.output}
      />
    : null;
  const lastIsAssistant = messageHistory.length > 0 && messageHistory[messageHistory.length - 1].role === 'assistant';

  return (
    <>
      {messageHistory.map((message: any, index) => {
        if (message.role === "user") {
          const _len = messageHistory.length;
          const isLastOrSecondToLast = index === _len - 1 || index === _len - 2;
          const editingThis = isEditing && editingMessageId === index;
          return (
            <UserMessageRow
              key={index}
              message={message}
              index={index}
              isLastOrSecondToLast={isLastOrSecondToLast}
              copied={copiedIndex === index}
              isEditing={isEditing}
              editingThis={editingThis}
              editValue={editingThis ? messageBeingEdited : ''}
              editBusy={editingThis && loadingGeneration}
              isMobile={isMobile}
              numPages={numPages}
              filePreviews={filePreviews}
              fileTextPreviews={fileTextPreviews}
              fileDownloadProgress={fileDownloadProgress}
              handlersRef={handlersRef}
            />
          );
        } else if (message.role === "assistant") {
          const isLast = index === messageHistory.length - 1;
          return (
            <AssistantMessageRow
              key={index}
              message={message}
              index={index}
              isLast={isLast}
              todoSuperseded={lastTodoIdx !== -1 && index !== lastTodoIdx}
              copied={copiedIndex === index}
              menuOpen={moreMenuIndex === index}
              hideActionButtons={isLast && (loadingGeneration || !!loadingMessage || !!messageBeingGenerated)}
              showRegenerate={isLast && !loadingGeneration}
              isStreamingThis={isLast && (loadingGeneration || !!loadingMessage || !!messageBeingGenerated)}
              drActive={!!deepResearchPhase || !!panoramaPhase}
              loadingGeneration={loadingGeneration}
              loadingMessage={loadingMessage}
              showInlineDebug={isLast && debugMode && currentToolCalls.length > 0 && !!messageBeingGenerated}
              currentToolCalls={currentToolCalls}
              liveProgress={isLast ? liveResearchCard : null}
              handlersRef={handlersRef}
              fmtRef={fmtRef}
            />
          );
        }
        return null;
      })}

      {liveResearchCard && !lastIsAssistant && (
        <BotMessageStrip>
          <MessageStripContentArea>
            {/* width 100%: a área de texto é um item flex que encolhe para o
                conteúdo; o card precisa da coluna inteira para não redimensionar
                a cada mudança do texto da etapa. */}
            <MessageStripTextArea style={{ width: '100%' }}>
              {liveResearchCard}
            </MessageStripTextArea>
          </MessageStripContentArea>
        </BotMessageStrip>
      )}

      {(loadingGeneration || loadingMessage) && !messageBeingGenerated && !(deepResearchPhase && deepResearchPhase !== 'done') && !(panoramaPhase && panoramaPhase !== 'done') && (() => {
        // A shimmering activity label ("Pensando", "Pesquisando na web", ...)
        // is already on screen whenever the last assistant message has a
        // pending item — a spinner below it is a redundant second indicator.
        const lastMsg: any = messageHistory[messageHistory.length - 1];
        const hasLiveActivityIndicator = !!(
          lastMsg && lastMsg.role === 'assistant' && Array.isArray(lastMsg.content) && (
            lastMsg.content.some((it: any) => it && it.pending) ||
            // The trailing thinking group holds "Pensando" through the gap
            // between the draft finishing and the answer text starting — it IS
            // the activity indicator there ("Pensou"+spinner looked broken).
            (loadingGeneration && trailingThinkingRunActive(compactAgentContent(lastMsg.content, true)))
          )
        );
        if (hasLiveActivityIndicator) return null;
        // The last answer is already on screen and its row draws this spinner
        // under its own content: a second ring here doubled it.
        if (loadingGeneration && assistantRowRendersContent(lastMsg)) return null;
        /* Same strip wrappers as a message bubble so the spinner starts at the
           text column (it used to render bare, visibly offset from the
           "Pensando" header above and from where the answer text begins). */
        return (
          <BotMessageStrip>
            <MessageStripContentArea>
              <MessageStripTextArea>
                {extractingFiles.length > 0
                  ? <ExtractingFilesBlock files={extractingFiles} />
                  : <LoadingMessageExtracted loadingMessage={loadingMessage} />}
              </MessageStripTextArea>
            </MessageStripContentArea>
          </BotMessageStrip>
        );
      })()}

      {/* (Removed the separate pre-answer "Raciocínio" strip: reasoning blocks
          now render in-place via renderContentInOrder in the message bubble
          above — both during the "Pensando..." phase (the reasoning-only message
          renders because hasContent now counts reasoning) and while streaming.
          Keeping the old strip rendered the SAME reasoning a second time once a
          tool/text item appeared, causing duplicate "Raciocínio" boxes.) */}

      {debugMode && currentToolCalls.length > 0 && !messageBeingGenerated && (
        <BotMessageStrip>
          <MessageStripContentArea>
            <MessageStripTextArea>
              <ToolCallsDebugPanel toolCalls={currentToolCalls} isStreaming={loadingGeneration} />
            </MessageStripTextArea>
          </MessageStripContentArea>
        </BotMessageStrip>
      )}

      {activeComparison && activeComparison.chat_id === activeChatID?.chatID && (
        <ComparisonBlock
          comparison={activeComparison}
          onSubmit={submitComparisonAnnotation}
          onFinish={finishComparison}
          renderSlot={(slot: ComparisonSlot, isLeft: boolean) =>
            renderContentInOrder(slot.content, [], formatAssistantMessage, !slot.done, false, false, isLeft ? -100 : -101)}
        />
      )}

      {chatErrorMessage && (
        <ErrorMessageExtracted
          errorMessage={chatErrorMessage}
          allowRetry={allowErrorRetry}
          showPlanUpgrade={showPlanUpgradeOption}
          onRetry={handleRetryMessage}
          onShowPlans={() => setOpenSubscriptionModal(true)}
        />
      )}

      {/* Mensagens na fila deste chat: ficam abaixo da resposta em curso e
          saem sozinhas quando ela terminar. */}
      {queuedForChat(queuedMessages, activeChatID?.chatID || '').map((item) => (
        <QueuedMessageRow
          key={item.id}
          item={item}
          held={heldQueueChatIds.has(item.chatId)}
          onSendNow={sendQueuedNow}
          onEdit={editQueuedMessage}
          onRemove={removeQueuedMessage}
        />
      ))}

      {/* Geração interrompida pelo limite de ciclos agênticos: oferece retomar
          com um clique (equivalente a digitar "Continue de onde parou."). Só na
          última mensagem — um turno novo do usuário o remove naturalmente. */}
      {(() => {
        const lastMsg: any = messageHistory[messageHistory.length - 1];
        const show = lastMsg?.role === 'assistant'
          && lastMsg.max_cycles_interrupted
          && !loadingGeneration
          && !loadingMessage
          && !chatErrorMessage
          && !isEditing;
        if (!show) return null;
        return (
          <ContinueStrip>
            <ContinueButton onClick={handleContinueGeneration}>
              <PlayArrowIcon style={{ fontSize: '1rem', marginRight: '0.3rem' }} />
              Continuar
            </ContinueButton>
          </ContinueStrip>
        );
      })()}

      <EndOfChatMessageMarker ref={endOfChatMessageMarkerRef} data-chat-end-marker />

      <ShareModal
        open={shareModalOpen}
        onClose={() => { setShareModalOpen(false); setShareMessages(undefined); setShareMessageIndex(undefined); }}
        chatId={activeChatID?.chatID || ''}
        messagesToShare={shareMessages}
        messageIndex={shareMessageIndex}
      />
      {!loadingGeneration && <MobileBottomSpacer />}
    </>
  );
};

export default MessageList;
